// Exit panel — flip-vs-hold toggle and selling-cost line items.
// Mirrors src/components/ExitPanel.jsx in the React standalone.
//
// When isFlip is false, selling costs collapse to $0 in the math
// contract (see calculations.totalSellingCosts) — so a hold-don't-flip
// scenario hides the per-line inputs entirely.
//
// Selling-cost rows are scaffolded once per item id and updated in
// place; the percent input survives across renders so users can type
// "0.5" without the field clobbering partial decimals on each
// keystroke (the broadcastToPanels render fires after every host
// update, so without persistent rows the input would be destroyed
// every keystroke).

import {computeDealMetrics} from '../calculations';
import {IDealLineItem, IDealState} from '../defaults';
import {panelSection} from '../primitives';

export interface IExitPanelHost {
	getState(): IDealState;
	update(patch: Partial<IDealState>): void;
}

interface ISellingRowRefs {
	row: HTMLElement;
	input: HTMLInputElement;
	computedEl: HTMLElement;
}

export class ExitPanel {
	private rootEl: HTMLElement | null;
	private titleEl: HTMLElement | null = null;
	private bodyEl: HTMLElement | null = null;

	// Persistent surface
	private flipBtn: HTMLButtonElement | null = null;
	private holdBtn: HTMLButtonElement | null = null;
	private holdNote: HTMLElement | null = null;
	private sellingHeadEl: HTMLElement | null = null;
	private sellingListEl: HTMLElement | null = null;
	private sellingRows: Map<string, ISellingRowRefs> = new Map();
	private totalLabelEl: HTMLElement | null = null;
	private totalValueEl: HTMLElement | null = null;
	private totalRow: HTMLElement | null = null;

	constructor(private host: IExitPanelHost) {
		this.rootEl = document.getElementById('id_lb-deal-panel-exit');
		if (!this.rootEl) {
			return;
		}
		this.scaffoldShell();
		this.scaffoldBody();
		this.render();
	}

	render(): void {
		if (!this.bodyEl) {
			return;
		}
		const state = this.host.getState();
		const m = computeDealMetrics(state);
		const valueLabel = state.mode === 'build' ? 'ACV' : 'ARV';
		const isFlip = !!state.isFlip;

		if (this.titleEl) {
			this.titleEl.textContent = 'Exit Strategy';
		}

		// Toggle states
		if (this.flipBtn) {
			this.flipBtn.classList.toggle('lb-deal-exit__toggle-btn--active', isFlip);
		}
		if (this.holdBtn) {
			this.holdBtn.classList.toggle('lb-deal-exit__toggle-btn--active', !isFlip);
		}

		// Hold-mode visibility
		if (this.holdNote) {
			this.holdNote.style.display = isFlip ? 'none' : '';
		}
		if (this.sellingHeadEl) {
			this.sellingHeadEl.textContent = `Selling costs (% of ${valueLabel})`;
			this.sellingHeadEl.style.display = isFlip ? '' : 'none';
		}
		if (this.sellingListEl) {
			this.sellingListEl.style.display = isFlip ? '' : 'none';
		}
		if (this.totalRow) {
			this.totalRow.style.display = isFlip ? '' : 'none';
		}

		// Sync selling rows to current state.sellingCosts
		this.renderSellingRows(state, m);

		// Footer
		if (this.totalLabelEl) {
			this.totalLabelEl.textContent = 'Total selling costs';
		}
		if (this.totalValueEl) {
			const ratio = m.adjustedARV > 0 ? (m.totalSellingCosts / m.adjustedARV) * 100 : 0;
			this.totalValueEl.textContent = `${formatCurrency(m.totalSellingCosts)} (${ratio.toFixed(1)}% of ${valueLabel})`;
		}
	}

	private renderSellingRows(state: IDealState, m: ReturnType<typeof computeDealMetrics>): void {
		if (!this.sellingListEl) {
			return;
		}
		const presentIds = new Set<string>();
		m.sellingCostsComputed.forEach(c => {
			presentIds.add(c.id);
			let row = this.sellingRows.get(c.id);
			if (!row) {
				row = this.scaffoldSellingRow(c);
				this.sellingRows.set(c.id, row);
				this.sellingListEl!.appendChild(row.row);
			}
			// Update computed dollar display
			row.computedEl.textContent = formatCurrency(c.computed);
			// Update percent input value when not focused — preserves
			// in-flight typing like "0.5".
			if (document.activeElement !== row.input) {
				row.input.value = c.weight > 0 ? (c.weight * 100).toFixed(1) : '';
			}
		});
		for (const [id, row] of this.sellingRows.entries()) {
			if (!presentIds.has(id)) {
				row.row.remove();
				this.sellingRows.delete(id);
			}
		}
	}

	private scaffoldSellingRow(item: IDealLineItem & {computed: number}): ISellingRowRefs {
		const row = document.createElement('div');
		row.className = 'lb-deal-line-row';
		const label = document.createElement('span');
		label.className = 'lb-deal-line-row__label';
		label.textContent = item.label;
		row.appendChild(label);

		const input = this.percentInput(item.id, w => {
			const stateNow = this.host.getState();
			const next = (stateNow.sellingCosts || []).map(c =>
				c.id === item.id ? {...c, weight: w} : c,
			);
			this.host.update({sellingCosts: next});
		});
		row.appendChild(input);

		const pct = document.createElement('span');
		pct.className = 'lb-deal-exit__pct';
		pct.textContent = '%';
		row.appendChild(pct);

		const computedEl = document.createElement('span');
		computedEl.className = 'lb-deal-exit__computed';
		row.appendChild(computedEl);

		return {row, input, computedEl};
	}

	// Decimal-percent input that defers reformatting until blur, so
	// users can type "0.5" without the field clobbering the leading
	// zero on each keystroke. Mirrors the React PercentInput component.
	private percentInput(itemId: string, onCommit: (w: number) => void): HTMLInputElement {
		const input = document.createElement('input');
		input.type = 'text';
		input.inputMode = 'decimal';
		input.placeholder = '0.0';
		input.className = 'lb-deal-exit__pct-input';
		input.setAttribute('data-focus-key', `exit-pct-${itemId}`);
		input.addEventListener('blur', () => {
			const parsed = parseFloat(input.value);
			input.value = !Number.isFinite(parsed) || parsed === 0 ? '' : parsed.toFixed(1);
		});
		input.addEventListener('input', () => {
			const cleaned = input.value.replace(/[^\d.]/g, '');
			const dots = (cleaned.match(/\./g) || []).length;
			let normalized = cleaned;
			if (dots > 1) {
				normalized = cleaned.slice(0, cleaned.lastIndexOf('.'));
			}
			if (input.value !== normalized) {
				input.value = normalized;
			}
			const parsed = parseFloat(normalized);
			onCommit(!Number.isFinite(parsed) ? 0 : parsed / 100);
		});
		return input;
	}

	// ─── Scaffold ───

	private scaffoldShell(): void {
		if (!this.rootEl) {
			return;
		}
		const handles = panelSection({
			rootEl: this.rootEl,
			icon: 'home',
			title: 'Exit Strategy',
		});
		this.titleEl = handles.titleEl;
		this.bodyEl = handles.bodyEl;
	}

	private scaffoldBody(): void {
		if (!this.bodyEl) {
			return;
		}

		// Flip / Hold toggle (persistent)
		const toggle = document.createElement('div');
		toggle.className = 'lb-deal-exit__toggle';
		this.flipBtn = this.makeToggleBtn('Flip (sell)', () => {
			this.host.update({isFlip: true});
		});
		toggle.appendChild(this.flipBtn);
		this.holdBtn = this.makeToggleBtn('Hold (rent / refi)', () => {
			this.host.update({isFlip: false});
		});
		toggle.appendChild(this.holdBtn);
		this.bodyEl.appendChild(toggle);

		// Hold-mode note
		const note = document.createElement('p');
		note.className = 'lb-deal-exit__hold-note';
		note.textContent = 'Hold strategy — selling costs excluded from projected profit. Track carry through Holding Costs to model rental / refi yield.';
		note.style.display = 'none';
		this.holdNote = note;
		this.bodyEl.appendChild(note);

		// Selling cost heading + list (rows added in renderSellingRows)
		const head = document.createElement('div');
		head.className = 'lb-deal-exit__heading';
		this.sellingHeadEl = head;
		this.bodyEl.appendChild(head);

		const list = document.createElement('div');
		list.className = 'lb-deal-exit__list';
		this.sellingListEl = list;
		this.bodyEl.appendChild(list);

		// Total footer
		const totalRow = document.createElement('div');
		totalRow.className = 'lb-deal-line-row lb-deal-line-row--summary';
		this.totalRow = totalRow;
		const tLabel = document.createElement('span');
		tLabel.className = 'lb-deal-line-row__label';
		this.totalLabelEl = tLabel;
		totalRow.appendChild(tLabel);
		const tVal = document.createElement('span');
		tVal.className = 'lb-deal-line-row__value';
		this.totalValueEl = tVal;
		totalRow.appendChild(tVal);
		this.bodyEl.appendChild(totalRow);
	}

	private makeToggleBtn(label: string, onClick: () => void): HTMLButtonElement {
		const btn = document.createElement('button');
		btn.type = 'button';
		btn.className = 'lb-deal-exit__toggle-btn';
		btn.textContent = label;
		btn.addEventListener('click', onClick);
		return btn;
	}
}

function formatCurrency(n: number): string {
	if (!Number.isFinite(n)) {
		return '$0';
	}
	return n.toLocaleString('en-US', {style: 'currency', currency: 'USD', maximumFractionDigits: 0});
}
